import {
  configure,
  DataCaptureContext,
  DataCaptureView,
  Logger,
  RectangularViewfinder,
  RectangularViewfinderLineStyle,
  RectangularViewfinderStyle,
} from '@scandit/web-datacapture-core'
import {
  BarcodeCapture,
  BarcodeCaptureSettings,
  barcodeCaptureLoader,
  BarcodeCaptureOverlay,
  BarcodeCaptureOverlayStyle,
  BarcodeBatch,
  BarcodeBatchSettings,
  BarcodeBatchBasicOverlay,
  BarcodeBatchBasicOverlayStyle,
} from '@scandit/web-datacapture-barcode'

function getLicense() {
  if (import.meta.env.VITE_E2E === 'true') {
    return window.WebsdkDemos.getLicenseKeyByHostname('websdk.demos.scandit.com')
  }
  return (
    window.WebsdkDemos.getLicenseKeyByHostname(window.location.hostname, true) ||
    import.meta.env.VITE_SCANDIT_LICENSE_KEY
  )
}

export class SDK {
  barcodeCaptureSettings: BarcodeCaptureSettings | undefined
  barcodeCaptureMode: BarcodeCapture | undefined
  barcodeBatchMode: BarcodeBatch | undefined
  barcodeBatchSettings: BarcodeBatchSettings | undefined
  view?: DataCaptureView
  context?: DataCaptureContext

  public async configure() {
    this.view = new DataCaptureView()
    this.view.connectToElement(document.getElementById('data-capture-view')!)
    const license = getLicense()
    await configure({
      licenseKey: license,
      libraryLocation: new URL('library/engine', document.baseURI).toString(),
      moduleLoaders: [barcodeCaptureLoader({ highEndBlurryRecognition: false })],
      logLevel: Logger.Level.Debug,
    })
    this.context = await DataCaptureContext.create()
    await this.view.setContext(this.context)
    return this
  }

  public async configureBatchMode(): Promise<{ mode: BarcodeBatch; settings: BarcodeBatchSettings }> {
    await this.context?.removeAllModes()
    this.barcodeBatchSettings = new BarcodeBatchSettings()
    this.barcodeBatchMode = await BarcodeBatch.forContext(this.context!, this.barcodeBatchSettings)
    await this.barcodeBatchMode.setEnabled(false)
    await BarcodeBatchBasicOverlay.withBarcodeBatchForViewWithStyle(
      this.barcodeBatchMode,
      this.view,
      BarcodeBatchBasicOverlayStyle.Frame
    )
    return { mode: this.barcodeBatchMode, settings: this.barcodeBatchSettings }
  }

  public async configureCaptureMode(): Promise<{ mode: BarcodeCapture; settings: BarcodeCaptureSettings }> {
    await this.context?.removeAllModes()

    this.barcodeCaptureSettings = new BarcodeCaptureSettings()
    this.barcodeCaptureMode = await BarcodeCapture.forContext(this.context!, this.barcodeCaptureSettings)
    await this.barcodeCaptureMode.setEnabled(false)

    const barcodeCaptureOverlay: BarcodeCaptureOverlay = await BarcodeCaptureOverlay.withBarcodeCaptureForViewWithStyle(
      this.barcodeCaptureMode,
      this.view!,
      BarcodeCaptureOverlayStyle.Frame
    )

    const viewFinder: RectangularViewfinder = new RectangularViewfinder(
      RectangularViewfinderStyle.Square,
      RectangularViewfinderLineStyle.Light
    )

    await barcodeCaptureOverlay.setViewfinder(viewFinder)
    return { mode: this.barcodeCaptureMode, settings: this.barcodeCaptureSettings }
  }

  public getCurrentMode(): BarcodeCapture | BarcodeBatch | undefined {
    if (this.barcodeCaptureMode != null) {
      return this.barcodeCaptureMode
    }
    if (this.barcodeBatchMode != null) {
      return this.barcodeBatchMode
    }
  }
}

export default new SDK()
